import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { ArrowLeft, Plus, Edit, Trash2, Search, User } from "lucide-react";

const TablasEmpleados = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [workCenters, setWorkCenters] = useState([]);
  const [systemUsers, setSystemUsers] = useState([]);
  const [areasResponsables, setAreasResponsables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    nombre: "", puesto: "", departamento: "", email: "", telefono: "", centro_trabajo_id: "", user_id: "",
  });

  useEffect(() => {
    fetchItems();
    fetchWorkCenters();
    fetchAreasResponsables();
    fetchSystemUsers();
  }, []);

  const fetchSystemUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`, { withCredentials: true });
      setSystemUsers(res.data || []);
    } catch (err) { console.error("Error fetching users:", err); }
  };

  const fetchItems = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/employees`, { withCredentials: true });
      setItems(res.data);
    } catch (err) { console.error("Error loading employees:", err); toast.error("Error al cargar"); }
    finally { setLoading(false); }
  };

  const fetchWorkCenters = async () => {
    try {
      const res = await axios.get(`${API}/work-centers`, { withCredentials: true });
      setWorkCenters(res.data);
    } catch (err) { console.error("Error fetching work centers:", err); }
  };

  const fetchAreasResponsables = async () => {
    try {
      const res = await axios.get(`${API}/settings`, { withCredentials: true });
      const areas = res.data?.areas_responsables || [];
      setAreasResponsables(areas);
    } catch (err) {
      console.error("Error fetching areas responsables:", err);
      // Fallback: try without auth check
      setAreasResponsables([]);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await axios.put(`${API}/employees/${editing.id}`, form, { withCredentials: true });
        toast.success("Empleado actualizado");
      } else {
        await axios.post(`${API}/employees`, form, { withCredentials: true });
        toast.success("Empleado creado");
      }
      setShowDialog(false);
      setEditing(null);
      setForm({ nombre: "", puesto: "", departamento: "", email: "", telefono: "", centro_trabajo_id: "", user_id: "" });
      fetchItems();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al guardar"); }
  };

  const handleEdit = (item) => {
    setEditing(item);
    setForm({
      nombre: item.nombre || "", puesto: item.puesto || "", departamento: item.departamento || "",
      email: item.email || "", telefono: item.telefono || "", centro_trabajo_id: item.centro_trabajo_id || "",
      user_id: item.user_id || "",
    });
    fetchAreasResponsables();
    fetchSystemUsers();
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este empleado?")) return;
    try {
      await axios.delete(`${API}/employees/${id}`, { withCredentials: true });
      toast.success("Empleado eliminado");
      fetchItems();
    } catch (err) { console.error("Error deleting employee:", err); toast.error("Error al eliminar"); }
  };

  const openNew = () => {
    setEditing(null);
    setForm({ nombre: "", puesto: "", departamento: "", email: "", telefono: "", centro_trabajo_id: "", user_id: "" });
    fetchAreasResponsables();
    fetchSystemUsers();
    setShowDialog(true);
  };

  const getCenterName = (id) => workCenters.find(c => c.id === id)?.nombre || "—";

  const filtered = items.filter(i =>
    i.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    i.puesto?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    i.departamento?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="tablas-empleados-page">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                <User className="w-6 h-6 text-blue-600" /> Empleados
              </h1>
              <p className="text-slate-500">Persona Física — Personal responsable de resguardo de activos fijos</p>
            </div>
          </div>
          <Button onClick={openNew} className="bg-blue-600 hover:bg-blue-700" data-testid="new-employee-btn">
            <Plus className="w-4 h-4 mr-2" /> Nuevo Empleado
          </Button>
        </div>

        <Card>
          <CardContent className="p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar por nombre, puesto o departamento..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-employees"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>NOMBRE</TableHead>
                  <TableHead>PUESTO</TableHead>
                  <TableHead>DEPARTAMENTO</TableHead>
                  <TableHead>EMAIL</TableHead>
                  <TableHead>TELÉFONO</TableHead>
                  <TableHead>CENTRO DE TRABAJO</TableHead>
                  <TableHead className="text-right">ACCIONES</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-10">Cargando...</TableCell></TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-500">No hay empleados</TableCell></TableRow>
                ) : (
                  filtered.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">{item.nombre}</TableCell>
                      <TableCell>{item.puesto}</TableCell>
                      <TableCell>{item.departamento}</TableCell>
                      <TableCell>{item.email}</TableCell>
                      <TableCell>{item.telefono}</TableCell>
                      <TableCell className="text-sm text-slate-500">{getCenterName(item.centro_trabajo_id)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="icon" variant="ghost" onClick={() => handleEdit(item)} data-testid={`edit-emp-${item.id}`}>
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button size="icon" variant="ghost" className="text-red-600" onClick={() => handleDelete(item.id)} data-testid={`delete-emp-${item.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) setEditing(null); }}>
          <DialogContent>
            <DialogHeader><DialogTitle>{editing ? "Editar Empleado" : "Nuevo Empleado"}</DialogTitle></DialogHeader>
            <form onSubmit={handleSave} className="space-y-4">
              <div className="space-y-2">
                <Label>Nombre *</Label>
                <Input required value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="emp-nombre" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Puesto</Label>
                  <Input value={form.puesto} onChange={(e) => setForm({ ...form, puesto: e.target.value })} data-testid="emp-puesto" />
                </div>
                <div className="space-y-2">
                  <Label>Departamento</Label>
                  <Select value={form.departamento || "none"} onValueChange={(v) => setForm({ ...form, departamento: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="emp-departamento"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">-- Sin asignar --</SelectItem>
                      {areasResponsables.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {areasResponsables.length === 0 && (
                    <p className="text-xs text-amber-600">Configura departamentos en Configuración → Opciones → Empleados</p>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="emp-email" />
                </div>
                <div className="space-y-2">
                  <Label>Teléfono</Label>
                  <Input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} data-testid="emp-telefono" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Centro de Trabajo</Label>
                <Select value={form.centro_trabajo_id || "none"} onValueChange={(v) => setForm({ ...form, centro_trabajo_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="emp-centro-select"><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-- Sin asignar --</SelectItem>
                    {workCenters.map(c => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Usuario del sistema (opcional)</Label>
                <Select value={form.user_id || "none"} onValueChange={(v) => setForm({ ...form, user_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="emp-user-select"><SelectValue placeholder="Sin usuario asignado" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">-- Sin usuario asignado --</SelectItem>
                    {systemUsers.map(u => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name || u.email} · {u.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">Enlaza este empleado con su cuenta para que sus acciones queden trazadas y aparezca en dropdowns como &quot;Agente&quot; en Cabina de Ventas.</p>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
                <Button type="submit" data-testid="save-emp-btn">{editing ? "Guardar Cambios" : "Crear"}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default TablasEmpleados;
